import { useState, type ComponentProps } from "react";
import { Plus, Trash2, Sliders } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ProfileForm } from "./ProfileForm";
import {
  useProfiles,
  useCreateProfile,
  useDeleteProfile,
} from "@/hooks/useProfiles";
import type { Profile } from "@/hooks/useProfiles";

export const ProfileList = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteProfileId, setDeleteProfileId] = useState<string | null>(null);

  const { data: profiles = [], isLoading, isError } = useProfiles();
  const createProfile = useCreateProfile();
  const deleteProfile = useDeleteProfile();

  const handleCreate: ComponentProps<typeof ProfileForm>["onSubmit"] = (
    data,
  ) => {
    createProfile.mutate(data as Parameters<typeof createProfile.mutate>[0], {
      onSuccess: () => setIsCreateDialogOpen(false),
    });
  };

  const handleDelete = () => {
    if (!deleteProfileId) return;
    deleteProfile.mutate(deleteProfileId, {
      onSuccess: () => setDeleteProfileId(null),
    });
  };

  if (isError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8 text-destructive">
          <Sliders className="h-8 w-8 mb-2" />
          <p>Failed to load profiles</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Encoding Profiles</h3>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Profile
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading profiles...
          </CardContent>
        </Card>
      ) : profiles.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Sliders className="h-8 w-8 mb-2" />
            <p>No profiles configured</p>
            <p className="text-sm">Add an encoding profile for your streams</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {profiles.map((profile) => (
            <ProfileCard
              key={profile.id}
              profile={profile}
              onDelete={() => setDeleteProfileId(profile.id)}
            />
          ))}
        </div>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Encoding Profile</DialogTitle>
          </DialogHeader>
          <ProfileForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateDialogOpen(false)}
            isLoading={createProfile.isPending}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteProfileId}
        onOpenChange={(open) => !open && setDeleteProfileId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Profile?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this encoding profile. Streams using
              this profile will be unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

interface ProfileCardProps {
  profile: Profile;
  onDelete: () => void;
}

const ProfileCard = ({ profile, onDelete }: ProfileCardProps) => {
  const resolution =
    profile.video_width && profile.video_height
      ? `${profile.video_width}x${profile.video_height}`
      : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-base">{profile.name}</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-destructive hover:text-destructive h-6 w-6 p-0"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-1">
          {resolution && <Badge variant="secondary">{resolution}</Badge>}
          {profile.video_framerate && (
            <Badge variant="secondary">{profile.video_framerate}fps</Badge>
          )}
          {profile.video_bitrate && (
            <Badge variant="secondary">
              {profile.video_bitrate >= 1000
                ? `${(profile.video_bitrate / 1000).toFixed(1)} Mbps`
                : `${profile.video_bitrate} kbps`}
            </Badge>
          )}
          {profile.video_codec && (
            <Badge variant="outline">{profile.video_codec}</Badge>
          )}
          {profile.audio_codec && (
            <Badge variant="outline">{profile.audio_codec}</Badge>
          )}
          {profile.audio_bitrate && (
            <Badge variant="outline">{profile.audio_bitrate} kbps audio</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
